import { QueryKey, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { format, isAfter, subDays } from 'date-fns';
import { isArray, isEmpty, isNil } from 'lodash';
import { PasswordChangeFormType } from '../app/PasswordChangePage';
import {
  AccountingDocument,
  AccountingDocumentsInvoice,
  CashReceipt
} from '../app/accountingDocuments/type/accountingDocuments';
import { SavedPrefs } from '../app/dealerStatement/hooks/useGridColumnPreferences';
import { FormFieldBaseType } from '../app/dynamicForm';
import { resetPasswordAction, userActionConfirm } from '../app/hooks';
import { RegistryAccidentResponse, RegistryAccidentsFilter } from '../app/registry/type';
import {
  CancelSourceCommissionsRequest,
  CancelSourceCommissionsResult,
  CreateSourceCommissionDocumentsRequest,
  CreateSourceCommissionDocumentsResult,
  SourceCommission,
  SourceCommissionDocument,
  SourceCommissionDocumentDetail,
  SourceCommissionItemInput,
  SourceCommissionPreviewResult,
  SourceCommissionsFilter
} from '../app/sourceCommissions/type/sourceCommissions';
import { getErrorCode, getErrorMessage } from '../app/sourceCommissions/utils';
import {
  Article,
  DocumentMeta,
  EventStatus,
  FileNode,
  RequestStatus,
  Role,
  User,
  UserEvent,
  UserQuestionnaire,
  UserQuestionnaireAnswer,
  UserRequest
} from '../app/types';
import {
  ACCIDENT_SOURCE,
  Accident,
  AccidentExistingUserResponse,
  AccidentGeneral,
  AccidentRequest,
  AccidentSourcesList,
  AccidentsResponse,
  AddNewAccountRequest,
  AresResponse,
  OpenServices,
  RegistryType,
  ServiceOrderFormFile,
  ServiceOrderFormResult,
  TenantType,
  UploadDocumentRequest,
  UserEmailAccountResponse,
  VehicleResponse
} from '../app/types/accident';
import { EmailForm } from '../app/types/form';
import { EstimatedPayment, Income } from '../app/types/income';
import { RegistryRequest } from '../app/types/registry';
import {
  AvailableRentalCars,
  CarRental,
  CarRentalRequest,
  CarRentalStatus,
  CarRentalsResponse,
  DocumentSignRequest,
  RENT_FILES,
  RentCarRequest,
  RentCarUpdateRequest,
  RentalCar,
  RentalStartType,
  RentalTakeoverRequest
} from '../app/types/rental';
import { StatementRequest } from '../app/types/statement';
import {
  DashboardStatistics,
  DealerRewards,
  DealerStatementResult,
  DealerStatistics,
  PeriodProgress
} from '../app/types/statistics';
import { RENTAL_CAR_DETAIL_QUERY_KEY } from '../constants';
import { env } from '../env';
import { useNotify } from '../hooks';
import { SeznamAutocomplete, SeznamAutocompleteResponse } from '../types/external/seznamAutocomplete';
import { qsUrl } from '../utils';
import { api } from './api';
import { RENTAL_CAR_INFO_KEY, STALE_TIME } from './constants';
import { mutation, query } from './reactQuery';
import { ApiResponse } from './types';
import {
  onSuccessCustomApiResponse,
  parseDate,
  parseDocumentType,
  parseFormData,
  parseId,
  parseUserName
} from './utils';

const ROOT_KEY = ['common'];

const ENDPOINTS = {
  LOGIN: '/users/loginUser',
  PASSWORD_RESET: '/users/resetPassword',
  PASSWORD_CHANGE: '/users/changePassword',
  USER_ACTION: (action: resetPasswordAction) => `/users/${action}`,
  CHECK_LOGIN: '/_rpsCzech/user',
  ARTICLES: '/_btlNet/posts',
  GET_ARTICLE: (articleId: string) => `/_btlNet/posts/${articleId}`,
  ARTICLE_MARK_AS_READ: '/_btlNet/articleViewed',
  GET_FORMS: '/_btlNet/forms',
  GET_FORM: (formId: string) => `/_btlNet/forms/${formId}`,
  SUBMIT_FORM: (formId: string) => `/_btlNet/forms/${formId}`,
  GET_REQUESTS: '/_btlNet/applications',
  SET_REQUEST_STATUS: '/_btlNet/applicationStatus',
  CANCAL_REQUEST: '/_btlNet/applicationCancel',
  GET_DOCUMENTS: (folderId?: string) => `/_btlNet/documents${folderId ? `/${folderId}` : ''}`,
  UPLOAD_DOCUMENTS: '/_btlNet/uploadFile',
  GET_RECENT_DOCUMENTS: '/_btlNet/documentsWidget',
  EDIT_DOCUMENT: (documentId: string) => `/_btlNet/file/${documentId}`,
  GET_EVENTS: (eventId?: string) => `/_btlNet/events${eventId ? `/${eventId}` : ''}`,
  SET_EVENT_STATUS: '/_btlNet/eventStatus',
  GET_QUESTIONNARIES: (questinaireId?: string) => `/_btlNet/questionnaires${questinaireId ? `/${questinaireId}` : ''}`,
  GET_NOTIFICATIONS: '/_btlNet/notifications',
  SET_NOTIFICATION_STATUS: (id: number) => `/_btlNet/notifications/${id}`,
  ACCIDENTS: (accidentId?: string) => `/_rpsCzech/accidents${accidentId ? `/${accidentId}` : ''}`,
  GET_ACCIDENTS_WAITING: '/_rpsCzech/accidentsWaiting',
  GET_REGISTRY_LIST: (type: RegistryType) => `/_rpsCzech/registry/${type}`,
  UPLOAD_ACCIDENT_FILE: () => '/_rpsCzech/upload',
  SEND_FORM: () => '/emailTemplates/sendEmail',
  GET_ARES_DATA: (vatNumber: string) => `/_rpsCzech/subjectAres/${vatNumber}`,
  DELETE_ACCIDENT_FILE: (accidentId: string, template: string, id: number) =>
    `/_rpsCzech/media/${accidentId}/${template}/${id}`,
  GET_DEALER_STATISTICS: () => '/_rpsCzech/dealerStatistics',
  GET_DEALER_STATEMENT_DATA: () => '/_rpsCzech/dealerAccidentsData',
  GET_DASHBOARD_STATISTICS: () => '/_rpsCzech/dashboardStatistics',
  GET_CAR_DATA: (orv: string) => `/_rpsCzech/carTechnicalData/${orv}`,
  CAR_RENTALS: (value?: number) => `/_rpsCzech/carRentals${value ? `/${value}` : ''}`,
  CAR_RENTAL_CAR_PARAMETERS: (value: number) => `/_rpsCzech/carRentalCar/${value}`,
  GET_AVAILABLE_RENTALS_CARS: (value?: number) => `/_rpsCzech/carRentalAvailableCars${value ? `/${value}` : ''}`,
  RENTAL_DOCUMENT: (documentName: string) => `/_rpsCzech/carRentalDocument/${documentName}`,
  DEALER_STATEMENT: (value?: string) => `/_rpsCzech/dealerStatement${value ? `/${value}` : ''}`,
  ADD_INSURANCE_COMPANY: () => '/_rpsCzech/insuranceCompany',
  REJECT_DEALER_STATEMENT_ACCIDENT: (statementId: number) => `/_rpsCzech/rejectDealerStatementAccident/${statementId}`,
  HIDE_DEALER_STATEMENT_ACCIDENT: (statementId: number) => `/_rpsCzech/hideDealerStatementAccident/${statementId}`,
  DEALER_REWARDS: (year: string) => `/_rpsCzech/dealerRewards/${year}`,
  INCOMES: (year: string) => `/_rpsCzech/dealerPayments/${year}`,
  ACCIDENT_DOCUMENT: (document: string) => `/_rpsCzech/accidentDocument/${document}`,
  OPEN_SERVICES: () => '/_rpsCzech/openServices',
  ACCIDENT_SERVICE: (accidentId: number) => `/_rpsCzech/accidentService/${accidentId}`,
  ACCIDENT_SERVICE_ORDER_FORM: (accidentId: number) => `/_rpsCzech/accidentServiceOrderForm/${accidentId}`,
  ACCIDENT_SOURCES: (source?: ACCIDENT_SOURCE | number) => `/_rpsCzech/accidentSources/${source}`,
  ACCIDENT_BY_SOURCES: (sourceId: number) => `/_rpsCzech/accidentsBySource/${sourceId}`,
  EXISTING_USER: () => '/_rpsCzech/getExistingUser',
  ACCOUNTING_DOCUMENTS: (receiptId?: number) => `/_rpsCzech/accountingDocuments${receiptId ? `/${receiptId}` : ''}`,
  SOURCE_COMMISSIONS: () => '/_rpsCzech/sourceCommissions',
  SOURCE_COMMISSION_PREVIEW: () => '/_rpsCzech/sourceCommissionDocumentPreview',
  SOURCE_COMMISSION_CANCEL: () => '/_rpsCzech/sourceCommissionCancel',
  SOURCE_COMMISSION_DOCUMENTS: (id?: number) => `/_rpsCzech/sourceCommissionDocuments${id ? `/${id}` : ''}`,
  ACCOUNTING_DOCUMENTS_INVOICES: () => '/_rpsCzech/accountingDocumentInvoices',
  GET_ACCOUNT_EMAIL: () => '/_rpsCzech/accountEmail',
  ACCOUNT: () => '/_rpsCzech/account',
  DEALER_STATEMENT_GRID: () => '/_rpsCzech/dealerStatementsDataGrid',
  DEALER_PHM_PRICE: () => '/_rpsCzech/setDealerAvgPhmPrice',
  DEALER_SOURCE: (sourceId: number) => `/_rpsCzech/dealerSource/${sourceId}`,
  ESTIMATED_PAYMENT: () => '/_rpsCzech/estimatedPayment',
  PERIOD_PROGRESS: () => '/_rpsCzech/periodProgress'
};

export interface UserResponse {
  id: number;
  email: string;
  name: string;
  phone: string;
  surname: string;
  accidentInProgress?: number;
  fullName?: string;
  account: {
    id: number;
    label: string;
    type: Role;
  };
  address: {
    city: string;
    street: string;
    zip: string;
    houseNum: string;
  };
  company: {
    companyIdNum: string;
    companyName: string;
    companyTaxNum: string;
  };
  statementsDataGrid?: string;
  avgPhmPrices?: Record<string, Record<string, number>>;
  phmLiters?: Record<string, Record<string, number>>;
}

interface ArticleResponse {
  id: string;
  createdDate: string;
  title: string;
  description: string;
  rpsCzech: {
    isMandatory: boolean;
    isViewedByUser: boolean;
  };
  images: {
    head?: {
      url: string;
    };
  };
}

export enum FormType {
  Request = 'request',
  Questionnaire = 'questionnaire'
}

export interface Form {
  id: string;
  title: string;
  content: FormFieldBaseType[];
}

interface FormResponse {
  id: number;
  title: string;
  content: FormFieldBaseType[];
}

interface ApplicationResponse {
  id: number;
  title: string;
  createdDate: string;
  content: {
    name: string;
    label: string;
    value: string | string[];
  }[];
  form: {
    id: number;
    title: string;
  };
  status: {
    label: string;
    name: RequestStatus;
  };
  user: UserResponse;
}

interface QuestionnaireAnswerResponse {
  id: number;
  createdDate: string;
  content: {
    name: string;
    label: string;
    value: string | string[];
  }[];
  user: UserResponse;
}

interface QuestionnaireResponse {
  id: number; //formId
  title: string;
  createdDate: string;
  form: {
    id: string;
    title: string;
  };
  event?: {
    id: number;
    title: string;
  };
  own?: QuestionnaireAnswerResponse;
  others: QuestionnaireAnswerResponse[];
}

interface DocumentResponse {
  id: number;
  name: string;
  fileUrl: string;
  type: string;
  thumbnailUrl?: string;
}

interface FileNodeResponse {
  folderId: number;
  parentId?: number;
  folderName?: string;
  data?: {
    folders?: {
      id: number;
      name: string;
    }[];
    files?: DocumentResponse[];
  };
}

interface RecentDocumentsResponse {
  totalFiles: number;
  files: DocumentResponse[];
}

interface EventResponse {
  id: number;
  dateFrom: string;
  dateTo: string;
  place: string;
  title: string;
  description: string;
  userStatus: EventStatus | null;
  questionnaire?: QuestionnaireResponse;
  assignedUsers: {
    status: EventStatus | null;
    user: { id: number; name: string; surname: string };
  }[];
}

export enum NotificationModel {
  Article = 'Article',
  Document = 'Document',
  Request = 'Request',
  Event = 'Event',
  Questionnaire = 'Questionnaire'
}

export interface Notification {
  id: number;
  objectId: number;
  objectModel: NotificationModel;
  type: string;
  viewed: boolean;
  createdDate: string;
  extraData?: {
    title: string;
    isMandatory?: boolean;
    url: string;
  };
}

type HttpMethod = 'post' | 'put' | 'patch';

const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');

//#region parsers
const parseUserResponse = (data: UserResponse): User => ({
  id: parseId(data.account?.id),
  email: data.email,
  name: data.name,
  fullName: parseUserName({ firstname: data.name, lastname: data.surname }),
  role: data.account?.type,
  accidentInProgress: data.accidentInProgress,
  statementsDataGrid: data.statementsDataGrid,
  avgPhmPrices: data.avgPhmPrices,
  phmLiters: data.phmLiters
});

const parseApplicationResponse = (data: ApplicationResponse): UserRequest => ({
  id: parseId(data.id),
  createdDate: parseDate(data.createdDate),
  status: data.status.name,
  userName: parseUserName({
    firstname: data.user.name,
    lastname: data.user.surname
  }),
  title: data.form.title,
  answers: data.content.map(({ name, label, value }) => ({
    key: name,
    label,
    value
  }))
});

const parseQuestionnaireAnswer = ({
  id,
  createdDate,
  content,
  user
}: QuestionnaireAnswerResponse): UserQuestionnaireAnswer => ({
  id: parseId(id),
  createdDate: parseDate(createdDate),
  answers: isEmpty(content)
    ? []
    : content.map(({ name, label, value }) => ({
        key: name,
        label,
        value
      })),
  user: parseUserResponse(user)
});

const parseQuestionnaireResponse = ({
  id,
  title,
  createdDate,
  event,
  own,
  others
}: QuestionnaireResponse): UserQuestionnaire => ({
  id: parseId(id),
  title: title,
  createdDate: parseDate(createdDate),
  event: event
    ? {
        id: parseId(event.id),
        title: event.title
      }
    : undefined,
  own: own ? parseQuestionnaireAnswer(own) : undefined,
  others: others.map(parseQuestionnaireAnswer).filter(({ answers }) => !isEmpty(answers))
});

const parseArticle = ({
  id,
  createdDate: createdDateFromProps,
  title,
  images,
  description,
  rpsCzech
}: ArticleResponse): Article => {
  const today = new Date();
  const createdDate = parseDate(createdDateFromProps);
  const sevenDaysFromToday = subDays(today, 7);
  const createdWithinLast7Days = isAfter(createdDate, sevenDaysFromToday);

  return {
    id: parseId(id),
    createdDate,
    title,
    image: images.head?.url,
    htmlContent: description,
    isNew: createdWithinLast7Days,
    isMandatory: rpsCzech?.isMandatory && !rpsCzech.isViewedByUser
  };
};

const parseDocument = ({ id, name, fileUrl, type }: DocumentResponse): DocumentMeta => ({
  id: parseId(id),
  name,
  url: fileUrl,
  type: parseDocumentType(type)
});

const parseFileNode = (node: FileNodeResponse): FileNode => {
  const parsedFolderId = isNil(node.parentId) ? undefined : parseId(node.parentId);
  return {
    folderId: parseId(node.folderId),
    folderName: node.folderName,
    parentId: parsedFolderId === '0' ? 'root' : parsedFolderId,
    folders:
      node.data?.folders?.map(({ id, name }) => ({
        id: parseId(id),
        name
      })) ?? [],
    documents: node.data?.files?.map(parseDocument) ?? []
  };
};

const parseEvent = (event: EventResponse): UserEvent => ({
  id: parseId(event.id),
  title: event.title,
  dateFrom: parseDate(event.dateFrom),
  dateTo: parseDate(event.dateTo),
  place: event.place,
  isNew: false,
  content: event.description,
  status: event.userStatus,
  invitees: event.assignedUsers.map(({ status, user }) => ({
    ...user,
    status
  })),
  questionnaire: event.questionnaire ? parseQuestionnaireResponse(event.questionnaire) : undefined
});

const parseForm = ({ id, ...rest }: FormResponse): Form => ({
  id: parseId(id),
  ...rest
});
//#endregion

//#region queries
const getArticles = query(
  async ({ limit }: Partial<{ limit: number }> = {}) =>
    await api
      .get<ApiResponse<ArticleResponse[]>>(qsUrl(ENDPOINTS.ARTICLES, { visibility: true, limit }))
      .then((response) => response.data.results.map(parseArticle)),
  [...ROOT_KEY, 'articles'],
  { staleTime: STALE_TIME.STABLE }
);
const getArticle = query(
  async (articleId: string) => {
    const article = (await api.get<ApiResponse<ArticleResponse | []>>(ENDPOINTS.GET_ARTICLE(articleId))).data.results;
    return isArray(article) ? null : parseArticle(article);
  },
  [...ROOT_KEY, 'articles', 'article'],
  { staleTime: STALE_TIME.STABLE }
);

const getForm = query(
  async (formId: string, formType: FormType) => {
    const form = (await api.get<ApiResponse<FormResponse>>(qsUrl(ENDPOINTS.GET_FORM(formId), { type: formType }))).data
      .results;
    return form ? parseForm(form) : null;
  },
  [...ROOT_KEY, 'forms', 'form'],
  { staleTime: STALE_TIME.STABLE }
);

const getForms = query(
  async (formType: FormType) =>
    (await api.get<ApiResponse<FormResponse[]>>(qsUrl(ENDPOINTS.GET_FORMS, { type: formType }))).data.results.map(
      parseForm
    ),
  [...ROOT_KEY, 'forms'],
  { staleTime: STALE_TIME.STABLE }
);

const getRequests = query(
  async () =>
    (await api.get<ApiResponse<ApplicationResponse[]>>(ENDPOINTS.GET_REQUESTS)).data.results.map(
      parseApplicationResponse
    ),
  [...ROOT_KEY, 'requests'],
  { staleTime: STALE_TIME.STABLE }
);

const getQuestionnaries = query(
  async () =>
    (await api.get<ApiResponse<QuestionnaireResponse[]>>(ENDPOINTS.GET_QUESTIONNARIES())).data.results.map(
      parseQuestionnaireResponse
    ),
  [...ROOT_KEY, 'questionnaries', 'list'],
  { staleTime: STALE_TIME.STABLE }
);

const getQuestionnaire = query(
  async (questionaryId: string) =>
    parseQuestionnaireResponse(
      (await api.get<ApiResponse<QuestionnaireResponse>>(ENDPOINTS.GET_QUESTIONNARIES(questionaryId))).data.results
    ),
  [...ROOT_KEY, 'questionnaries'],
  { staleTime: STALE_TIME.STABLE }
);

const getDocuments = query(
  async (folderId?: string) =>
    parseFileNode((await api.get<ApiResponse<FileNodeResponse>>(ENDPOINTS.GET_DOCUMENTS(folderId))).data.results),
  [...ROOT_KEY, 'documents'],
  { staleTime: STALE_TIME.STABLE }
);

const getEvents = query(
  async ({ dateFrom, dateTo, limit }: Partial<{ dateFrom: Date; dateTo: Date; limit: number }> = {}) =>
    (
      await api.get<ApiResponse<EventResponse[]>>(
        qsUrl(ENDPOINTS.GET_EVENTS(), {
          visibility: true,
          dateFrom: dateFrom && formatDate(dateFrom),
          dateTo: dateTo && formatDate(dateTo),
          limit
        })
      )
    ).data.results.map(parseEvent),
  [...ROOT_KEY, 'events', 'list'],
  { staleTime: STALE_TIME.STABLE }
);

const getEvent = query(
  async (eventId: string) => {
    const event = (await api.get<ApiResponse<EventResponse | []>>(ENDPOINTS.GET_EVENTS(eventId))).data.results;
    return isArray(event) ? null : parseEvent(event);
  },
  [...ROOT_KEY, 'events', 'detail'],
  { staleTime: STALE_TIME.STABLE }
);

const getUser = query(
  async () => {
    const userData = (await api.get<ApiResponse<UserResponse>>(ENDPOINTS.CHECK_LOGIN)).data.results;
    return !isEmpty(userData) ? parseUserResponse(userData) : null;
  },
  [...ROOT_KEY, 'user'],
  { staleTime: STALE_TIME.SHORT }
);

const getRecentDocuments = query(
  async () => {
    const { totalFiles, files } = (await api.get<ApiResponse<RecentDocumentsResponse>>(ENDPOINTS.GET_RECENT_DOCUMENTS))
      .data.results;
    return {
      totalCount: totalFiles,
      documents: files.map(parseDocument)
    };
  },
  [...ROOT_KEY, 'documents', 'recent'],
  { staleTime: STALE_TIME.STABLE }
);

const getNotifications = query(
  async () => (await api.get<ApiResponse<Notification[]>>(ENDPOINTS.GET_NOTIFICATIONS)).data.results,
  [...ROOT_KEY, 'notifications'],
  { staleTime: STALE_TIME.STABLE }
);

const getAccidents = ({
  limit,
  offset,
  search,
  statusId
}: {
  limit: number;
  offset: number;
  search?: string;
  statusId?: number;
}) => ({
  queryKey: [...ROOT_KEY, 'accidents', { limit, offset, search, statusId }] as const,
  queryFn: async () =>
    (
      await api.get<ApiResponse<AccidentsResponse>>(
        qsUrl(ENDPOINTS.ACCIDENTS(), {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(statusId != null ? { statusId } : {})
        })
      )
    ).data.results,
  staleTime: STALE_TIME.SHORT
});
const getAccident = query(
  async (accidentId: string) => (await api.get<ApiResponse<Accident>>(ENDPOINTS.ACCIDENTS(accidentId))).data.results,
  [...ROOT_KEY, 'accidents', 'detail'],
  { staleTime: STALE_TIME.SHORT }
);
const getAccidentsWaiting = query(
  async () => (await api.get<ApiResponse<number[]>>(ENDPOINTS.GET_ACCIDENTS_WAITING)).data.results,
  [...ROOT_KEY, 'accidents', 'waiting'],
  { staleTime: STALE_TIME.SHORT }
);
const getRegistryList = query(
  async (type: RegistryType) => (await api.get<ApiResponse>(ENDPOINTS.GET_REGISTRY_LIST(type))).data.results,
  [...ROOT_KEY, 'registry'],
  { staleTime: STALE_TIME.STABLE }
);
const getDealerStatistics = query(
  async ({
    dealerId,
    dateTo,
    dateFrom
  }: {
    dateFrom: string;
    dateTo: string;
    dealerId: string;
  }) =>
    (
      await api.get<ApiResponse<DealerStatistics>>(
        qsUrl(ENDPOINTS.GET_DEALER_STATISTICS(), {
          dateFrom,
          dateTo,
          dealerId
        })
      )
    ).data.results,
  [...ROOT_KEY, 'statistics', 'dealer'],
  { staleTime: STALE_TIME.STABLE }
);
const getDealerStatementStatistics = query(
  async ({
    dealerId,
    dateTo,
    dateFrom
  }: {
    dateFrom: string;
    dateTo: string;
    dealerId: string;
  }) =>
    (
      await api.get<ApiResponse<DealerStatementResult>>(
        qsUrl(ENDPOINTS.GET_DEALER_STATEMENT_DATA(), {
          dateFrom,
          dateTo,
          dealerId
        })
      )
    ).data.results,
  [...ROOT_KEY, 'statistics', 'statement', 'dealer'],
  { staleTime: STALE_TIME.HALF_MINUTE }
);
const getDashboardStatistics = query(
  async () => (await api.get<ApiResponse<DashboardStatistics>>(ENDPOINTS.GET_DASHBOARD_STATISTICS())).data.results,
  [...ROOT_KEY, 'dashboard', 'statistics'],
  { staleTime: STALE_TIME.STABLE }
);

const getCarRentals = ({
  search,
  limit,
  offset,
  status
}: {
  search?: string;
  limit?: number;
  offset?: number;
  status?: CarRentalStatus;
} = {}) => ({
  queryKey: [...ROOT_KEY, 'rental', 'list', { limit, offset, search, status }] as const,
  queryFn: async () =>
    (
      await api.get<ApiResponse<CarRentalsResponse>>(
        qsUrl(ENDPOINTS.CAR_RENTALS(), {
          limit,
          offset,
          ...(search ? { search } : {}),
          ...(status ? { status } : {})
        })
      )
    ).data.results,
  staleTime: STALE_TIME.SHORT
});
const getRentalCar = query(
  async (rentalId: number) => (await api.get<ApiResponse<CarRental>>(ENDPOINTS.CAR_RENTALS(rentalId))).data.results,
  [...ROOT_KEY, 'rental', 'detail'],
  { staleTime: STALE_TIME.STABLE }
);
const getCarRentalsCarInfo = query(
  async (carId: number) =>
    (await api.get<ApiResponse<RentalCar>>(ENDPOINTS.CAR_RENTAL_CAR_PARAMETERS(carId))).data.results,
  [...ROOT_KEY, ...RENTAL_CAR_INFO_KEY],
  { staleTime: STALE_TIME.STABLE }
);
const getDealerRewards = query(
  async (year: string) => (await api.get<ApiResponse<DealerRewards>>(ENDPOINTS.DEALER_REWARDS(year))).data.results,
  [...ROOT_KEY, 'dealer', 'rewards'],
  { staleTime: STALE_TIME.DYNAMIC }
);
const getIncomes = query(
  async (year: string) => (await api.get<ApiResponse<Income[]>>(ENDPOINTS.INCOMES(year))).data.results,
  [...ROOT_KEY, 'dealer', 'incomes'],
  { staleTime: STALE_TIME.STABLE }
);
const getOpenServices = query(
  async () => (await api.get<ApiResponse<OpenServices[]>>(ENDPOINTS.OPEN_SERVICES())).data.results,
  [...ROOT_KEY, 'accident', 'services'],
  { staleTime: STALE_TIME.STABLE }
);
const getAccidentService = query(
  async (accidentId: number) =>
    (await api.get<ApiResponse<OpenServices>>(ENDPOINTS.ACCIDENT_SERVICE(accidentId))).data.results,
  [...ROOT_KEY, 'accident', 'service'],
  { staleTime: STALE_TIME.STABLE }
);
const getAccidentSourcesByType = (sourceType: ACCIDENT_SOURCE, favourites?: boolean, assigned?: boolean) => ({
  queryKey: [...ROOT_KEY, 'accident', 'sources', sourceType, { favourites, assigned }] as const,
  queryFn: async () =>
    (
      await api.get<ApiResponse<AccidentSourcesList>>(
        qsUrl(ENDPOINTS.ACCIDENT_SOURCES(sourceType), {
          favourites,
          assigned
        })
      )
    ).data.results,
  staleTime: STALE_TIME.STABLE
});
const getAccidentBySources = (sourceId: number, filter: RegistryAccidentsFilter = {}) => ({
  queryKey: [...ROOT_KEY, 'sources', 'accidents', sourceId, filter] as const,
  queryFn: async ({ signal }: { signal?: AbortSignal }) =>
    (
      await api.get<ApiResponse<RegistryAccidentResponse>>(
        qsUrl(ENDPOINTS.ACCIDENT_BY_SOURCES(sourceId), {
          commissionPaid: filter.commissionPaid,
          year: filter.year
        }),
        { signal }
      )
    ).data.results,
  staleTime: STALE_TIME.STABLE
});
const getCashReceipts = query(
  async () => (await api.get<ApiResponse<AccountingDocument[]>>(ENDPOINTS.ACCOUNTING_DOCUMENTS())).data.results,
  [...ROOT_KEY, 'accident', 'documents', 'cashReceipts'],
  { staleTime: STALE_TIME.STABLE }
);
const SOURCE_COMMISSIONS_KEY = [...ROOT_KEY, 'sourceCommissions'];
const ACCIDENT_SOURCES_KEY = [...ROOT_KEY, 'accident', 'sources'];
const getSourceCommissions = (filter: SourceCommissionsFilter = {}) => ({
  queryKey: [...SOURCE_COMMISSIONS_KEY, 'unpaid', filter] as const,
  queryFn: async ({ signal }: { signal?: AbortSignal }) =>
    (
      await api.get<ApiResponse<SourceCommission[]>>(
        qsUrl(ENDPOINTS.SOURCE_COMMISSIONS(), {
          year: filter.year,
          sourceId: filter.sourceId,
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo
        }),
        { signal }
      )
    ).data.results,
  staleTime: STALE_TIME.SHORT
});
const getSourceCommissionDocuments = ({ limit, offset }: { limit: number; offset: number }) => ({
  queryKey: [...SOURCE_COMMISSIONS_KEY, 'documents', { limit, offset }] as const,
  queryFn: async () =>
    (
      await api.get<ApiResponse<SourceCommissionDocument[]>>(
        qsUrl(ENDPOINTS.SOURCE_COMMISSION_DOCUMENTS(), { limit, offset })
      )
    ).data.results,
  staleTime: STALE_TIME.SHORT
});
const getSourceCommissionDocument = (id: number) => ({
  queryKey: [...SOURCE_COMMISSIONS_KEY, 'document', id] as const,
  queryFn: async () =>
    (await api.get<ApiResponse<SourceCommissionDocumentDetail>>(ENDPOINTS.SOURCE_COMMISSION_DOCUMENTS(id))).data
      .results,
  staleTime: 0
});
const getAccountingDocumentsInvoices = query(
  async () =>
    (await api.get<ApiResponse<AccountingDocumentsInvoice[]>>(ENDPOINTS.ACCOUNTING_DOCUMENTS_INVOICES())).data.results,
  [...ROOT_KEY, 'accident', 'accountingDocuments', 'invoices'],
  { staleTime: STALE_TIME.STABLE }
);
const getEstimatedPayments = query(
  async () => (await api.get<ApiResponse<EstimatedPayment>>(ENDPOINTS.ESTIMATED_PAYMENT())).data.results,
  [...ROOT_KEY, 'estimatedPayments'],
  { staleTime: STALE_TIME.STABLE }
);
// 402 (not a dealer) and 429 (no active period) mean "nothing to show" -> null
const getPeriodProgress = query(
  async () => {
    try {
      const { data } = await api.get<ApiResponse<PeriodProgress>>(ENDPOINTS.PERIOD_PROGRESS());
      return data.success ? data.results : null;
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      if (status === 402 || status === 429) return null;
      throw error;
    }
  },
  [...ROOT_KEY, 'periodProgress'],
  { staleTime: STALE_TIME.DYNAMIC, retry: false }
);
//#endregion

export const useCommonApi = () => {
  const { notify } = useNotify();
  const queryClient = useQueryClient();
  /** Zdroje (tipaři/servisy) se zobrazují v seznamech, doklady POJ i dialozích nehod, proto invalidace celé větve. */
  const invalidateSources = () => {
    queryClient.invalidateQueries({ queryKey: ACCIDENT_SOURCES_KEY });
    queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'sources', 'accidents'] });
    queryClient.invalidateQueries({ queryKey: SOURCE_COMMISSIONS_KEY });
  };

  const clearCache = (keys?: string[]) => queryClient.invalidateQueries({ queryKey: [ROOT_KEY, ...(keys ?? [])] });

  const update = <T>(queryKey: QueryKey, updater: (data: T) => T) => {
    const data: T | undefined = queryClient.getQueryData(queryKey);
    const updated = data && updater(data);
    if (data) queryClient.setQueryData(queryKey, updated);
    return updated;
  };

  return {
    login: () =>
      mutation(
        async ({ email, password }: { email: string; password: string }) =>
          (await api.post<ApiResponse>(ENDPOINTS.LOGIN, { email, password })).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            failed: (_, __, data) => {
              notify('error', 'Nesprávné jméno nebo heslo!' || 'Přihlášení se nezdařilo');
              console.error(data.message);
            }
          })
        }
      ),
    resetPassword: () =>
      mutation(
        async ({ identification }: { identification: string }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.PASSWORD_RESET, {
              identification
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Odkaz pro obnovení hesla byl zaslán do e-mailové schránky');
            },
            failed: (_, __, data) => {
              notify('error', data.message || 'Reset password failed');
              console.error(data.message);
            }
          })
        }
      ),
    changePassword: () =>
      mutation(
        async ({ currentPassword, newPassword, newPasswordAgain }: PasswordChangeFormType) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.PASSWORD_CHANGE, {
              currentPassword,
              newPassword,
              newPasswordAgain
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Heslo bylo úspěšně změněno');
            },
            failed: (_, __, data) => {
              notify('error', data.message || 'Změna hesla se nezdařila');
              console.error(data.message);
            }
          })
        }
      ),
    userAction: () =>
      mutation(
        async ({ action, identification, confirmCodeKey, hash }: userActionConfirm) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.USER_ACTION(action), {
              identification,
              [confirmCodeKey]: hash
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Heslo bylo úspěšně resetováno. Pošleme vám e-mail s novým heslem');
            },
            failed: (_, __, data) => {
              notify('error', data.message || 'Resetování hesla se nezdařilo');
              console.error(data.message);
            }
          })
        }
      ),
    markArticleAsRead: () =>
      mutation(
        async (articleId: string) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.ARTICLE_MARK_AS_READ, {
              articleId
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Article marked as read');
              queryClient.invalidateQueries({
                queryKey: getArticles().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', 'Article mark as read failed');
              console.error(data.message);
            }
          })
        }
      ),
    setRequestStatus: () =>
      mutation(
        async ({
          requestId,
          status
        }: {
          requestId: string;
          status: RequestStatus;
        }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.SET_REQUEST_STATUS, {
              applicationId: requestId,
              status
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: (variables) => {
              //optimistic update
              const requestsQueryKey = getRequests().queryKey;
              update<UserRequest[]>(requestsQueryKey, (requests) =>
                requests.map((r) =>
                  r.id === variables.requestId
                    ? {
                        ...r,
                        status: variables.status
                      }
                    : r
                )
              );
              queryClient.invalidateQueries({ queryKey: requestsQueryKey });
            }
          })
        }
      ),
    cancelRequest: () =>
      mutation(
        async ({ requestId }: { requestId: string }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.CANCAL_REQUEST, {
              applicationId: requestId
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getRequests().queryKey
              });
            }
          })
        }
      ),
    submitForm: () =>
      mutation(
        async ({
          formId,
          formType,
          data
        }: {
          formId: string;
          formType: FormType;
          data: Record<string, any>;
        }) =>
          (await api.post<ApiResponse>(qsUrl(ENDPOINTS.SUBMIT_FORM(formId), { type: formType }), { formData: data }))
            .data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Form submitted succesfully');
              queryClient.invalidateQueries({
                queryKey: getRequests().queryKey
              });
              queryClient.invalidateQueries({
                queryKey: getQuestionnaries().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', 'Form submission failed');
              console.error(data.message);
            }
          }),
          onError: (error) => {
            notify('error', 'Form submission failed');
            console.error(error);
          }
        }
      ),
    uploadDocuments: () =>
      mutation(
        async ({
          formData,
          folderId
        }: {
          formData: FormData;
          folderId?: string;
        }) => {
          const files = await parseFormData(formData);
          return (
            await api.post<ApiResponse>(ENDPOINTS.UPLOAD_DOCUMENTS, {
              files,
              folderId
            })
          ).data;
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Documents uploaded succesfully');
              queryClient.invalidateQueries({
                queryKey: getDocuments().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', 'Document upload failed');
              console.error(data.message);
            }
          }),
          onError: (error) => {
            console.error(error);
          }
        }
      ),
    editDocument: () =>
      mutation(
        async ({ documentId, name }: { documentId: string; name: string }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.EDIT_DOCUMENT(documentId), {
              name
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Document edited succesfully');
              queryClient.invalidateQueries({
                queryKey: getDocuments().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', 'Failed to edit document');
              console.error(data.message);
            }
          })
        }
      ),
    removeDocument: () =>
      mutation(
        async ({ documentId }: { documentId: string }) =>
          (await api.delete<ApiResponse>(ENDPOINTS.EDIT_DOCUMENT(documentId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Document removed succesfully');
            },
            failed: (_, __, data) => {
              notify('error', 'Document removal failed');
              console.error(data.message);
            }
          })
        }
      ),
    setEventStatus: () =>
      mutation(
        async ({
          eventId,
          status
        }: {
          eventId: string;
          status: EventStatus | null;
        }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.SET_EVENT_STATUS, {
              eventId,
              status
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: (variables) => {
              //optimistic update
              const eventsQueryKey = getEvents().queryKey;
              const eventDetailQueryKey = getEvent(variables.eventId).queryKey;
              update<UserEvent[]>(eventsQueryKey, (events) =>
                events.map((e) => (e.id === variables.eventId ? { ...e, status: variables.status } : e))
              );
              update<UserEvent>(eventDetailQueryKey, (event) => ({
                ...event,
                status: variables.status
              }));
              queryClient.invalidateQueries({ queryKey: eventsQueryKey });
              queryClient.invalidateQueries({ queryKey: eventDetailQueryKey });
            }
          })
        }
      ),
    setNotificationStatus: () =>
      mutation(
        async ({ id }: { id: number }) => (await api.put<ApiResponse>(ENDPOINTS.SET_NOTIFICATION_STATUS(id))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getNotifications().queryKey
              });
            }
          })
        }
      ),
    createAccident: () =>
      mutation(
        async (accidentGeneralData: AccidentGeneral) =>
          (
            await api.post<ApiResponse<{ id: string }>>(ENDPOINTS.ACCIDENTS(), {
              general: accidentGeneralData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getUser().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', data.message || 'Chyba při vytváření nehody');
              console.error(data.message);
            }
          })
        }
      ),
    updateAccident: () =>
      mutation(
        async ({
          id,
          accidentData
        }: {
          id: string;
          accidentData: AccidentRequest;
        }) =>
          (
            await api.patch<ApiResponse<Accident>>(ENDPOINTS.ACCIDENTS(id), {
              ...accidentData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getUser().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', data.message || 'Chyba při vytváření nehody');
              console.error(data.message);
            }
          })
        }
      ),
    uploadAccidentFiles: () =>
      mutation(
        async ({ filesData }: { filesData: UploadDocumentRequest }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.UPLOAD_ACCIDENT_FILE(), {
              ...filesData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getDocuments().queryKey
              });
            },
            failed: (_, __, data) => {
              notify('error', 'Chyba při nahrávání souborů');
              console.error(data.message);
            }
          }),
          onError: (error) => {
            notify('error', 'Chyba při nahrávání souborů');
            console.error(error);
          }
        }
      ),
    deleteAccidentFile: () =>
      mutation(
        async ({
          accidentId,
          template,
          id
        }: {
          accidentId: string;
          template: string;
          id: number;
        }) => (await api.delete<ApiResponse>(ENDPOINTS.DELETE_ACCIDENT_FILE(accidentId, template, id))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {},
            failed: (_, __, data) => {
              notify('error', 'Chyba při mazání souborů');
              console.error(data.message);
            }
          }),
          onError: (error) => {
            notify('error', 'Chyba při mazání souborů');
            console.error(error);
          }
        }
      ),
    sendEmailForm: () =>
      mutation<EmailForm, ApiResponse>(
        async (formData) => (await api.post<ApiResponse>(ENDPOINTS.SEND_FORM(), formData)).data,
        {
          onError: console.error
        }
      ),
    getARESData: () =>
      mutation<{ vatNumber: string }, ApiResponse<AresResponse>>(
        async ({ vatNumber }) => (await api.get<ApiResponse<AresResponse>>(ENDPOINTS.GET_ARES_DATA(vatNumber))).data,
        {
          onError: console.error
        }
      ),
    removeAccident: () =>
      mutation<{ accidentId: string }, ApiResponse>(
        async ({ accidentId }) => (await api.delete<ApiResponse>(ENDPOINTS.ACCIDENTS(accidentId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: getUser().queryKey
              });
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'accidents']
              });
            }
          }),
          onError: console.error
        }
      ),
    getCarData: () =>
      mutation<{ orv: string }, ApiResponse<VehicleResponse>>(
        async ({ orv }) => (await api.get<ApiResponse<VehicleResponse>>(ENDPOINTS.GET_CAR_DATA(orv))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: (_, data) => {
              if (!data.response) notify('error', 'Chyba při získávání dat!');
            }
          }),
          onError: console.error
        }
      ),
    getAvailableRentalCars: () =>
      mutation<CarRentalRequest, ApiResponse<AvailableRentalCars>>(
        async ({ searchPhrase, carParameters, returnDate, rentalDate, branchId }) => {
          const formattedRentalDate = rentalDate ? new Date(rentalDate).toISOString() : undefined;
          const formattedReturnDate = returnDate ? new Date(returnDate).toISOString() : undefined;

          return (
            await api.get<ApiResponse<AvailableRentalCars>>(
              qsUrl(ENDPOINTS.GET_AVAILABLE_RENTALS_CARS(), {
                searchPhrase,
                carParameters,
                returnDate: formattedReturnDate,
                rentalDate: formattedRentalDate,
                branchId
              })
            )
          ).data;
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            failed: () => {
              notify('error', 'Chyba při získávání dat k zápůjčce');
            }
          })
        }
      ),
    rentCar: () =>
      mutation<RentCarRequest, ApiResponse<AvailableRentalCars>>(
        async (rentData: RentCarRequest) =>
          (await api.post<ApiResponse<AvailableRentalCars>>(ENDPOINTS.CAR_RENTALS(), rentData)).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => notify('success', 'Zápůjčka byla úspěšná!'),
            failed: () => notify('error', 'Chyba při zpracovávání zápůjčky!')
          }),
          onError: console.error
        }
      ),
    removeRent: () =>
      mutation<{ rentId: number }, ApiResponse>(
        async ({ rentId }) => (await api.delete<ApiResponse>(ENDPOINTS.CAR_RENTALS(rentId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: ({ rentId }) => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'rental', 'list']
              });
              queryClient.invalidateQueries({
                queryKey: getRentalCar(rentId).queryKey
              });
            },
            failed: () => notify('error', 'Chyba při mazání zápůjčky!')
          }),
          onError: console.error
        }
      ),
    takeoverRent: () =>
      mutation<{ rentId: number; rentData: RentalTakeoverRequest }, ApiResponse>(
        async ({ rentData, rentId }) => {
          const returnDate: any = rentData?.rental?.returnDate;

          return (
            await api.put<ApiResponse>(ENDPOINTS.CAR_RENTALS(rentId), {
              ...rentData,
              rental: {
                returnDate: returnDate ? new Date(returnDate).toISOString() : undefined
              }
            })
          ).data;
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: ({ rentId }) => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'rental', 'list']
              });
              queryClient.invalidateQueries({
                queryKey: getRentalCar(rentId).queryKey
              });
            }
          }),
          onError: console.error
        }
      ),
    startRent: () =>
      mutation<{ rentId: number; data?: RentalStartType }, ApiResponse>(
        async ({ rentId, data }) => {
          const returnDate: any = data?.rental?.returnDate;
          const rentalDate: any = data?.rental?.rentalDate;
          return (
            await api.put<ApiResponse>(ENDPOINTS.CAR_RENTALS(rentId), {
              rentId,
              rental: {
                rentalDate: rentalDate ? new Date(rentalDate).toISOString() : undefined,
                returnDate: returnDate ? new Date(returnDate).toISOString() : undefined
              }
            })
          ).data;
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: ({ rentId }) => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'rental', 'list']
              });
              queryClient.invalidateQueries({
                queryKey: getRentalCar(rentId).queryKey
              });
            },
            failed: () => notify('error', 'Chyba u zpracovávání zápůjčky')
          }),
          onError: console.error
        }
      ),
    updateRent: () =>
      mutation<{ rentId: number; rentData: RentCarUpdateRequest }, ApiResponse<{ id: number }>>(
        async ({ rentData, rentId }) =>
          (
            await api.patch<ApiResponse<{ id: number }>>(ENDPOINTS.CAR_RENTALS(rentId), {
              ...rentData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: ({ rentId }) => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, ...RENTAL_CAR_DETAIL_QUERY_KEY, rentId]
              });
            },
            failed: () => notify('error', 'Chyba při ukládání zápůjčky')
          }),
          onError: console.error
        }
      ),
    regenerateRentalDocument: () =>
      mutation<
        {
          rentalDocumentType: RENT_FILES;
          rentalId: number;
          carParameters?: Record<string, any>;
          files?: DocumentSignRequest[];
        },
        ApiResponse
      >(
        async ({ rentalDocumentType, rentalId, files, carParameters }) =>
          (
            await api.patch<ApiResponse>(ENDPOINTS.RENTAL_DOCUMENT(rentalDocumentType), {
              rentalId,
              carParameters,
              files
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: ({ rentalId }) => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, ...RENTAL_CAR_DETAIL_QUERY_KEY, rentalId]
              });
            },
            failed: () => notify('error', 'Chyba při generování dokumentu')
          }),
          onError: console.error
        }
      ),
    addNewStatement: () =>
      mutation<{ statementData: StatementRequest }, ApiResponse>(
        async ({ statementData }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.DEALER_STATEMENT(), {
              ...statementData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'statistics', 'statement', 'dealer']
              });
            },
            failed: () => notify('error', 'Chyba při zakládání záznamu!')
          }),
          onError: console.error
        }
      ),
    updateCustomStatement: () =>
      mutation<{ statementId: number; statementData: StatementRequest }, ApiResponse>(
        async ({ statementId, statementData }) =>
          (
            await api.patch<ApiResponse>(ENDPOINTS.DEALER_STATEMENT(statementId.toString()), {
              ...statementData
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'statistics', 'statement', 'dealer']
              });
            },
            failed: () => notify('error', 'Chyba při úpravě záznamu!')
          }),
          onError: console.error
        }
      ),
    removeCustomStatement: () =>
      mutation<{ statementId: number }, ApiResponse>(
        async ({ statementId }) =>
          (await api.delete<ApiResponse>(ENDPOINTS.DEALER_STATEMENT(statementId.toString()))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'statistics', 'statement', 'dealer']
              });
            },
            failed: () => notify('error', 'Chyba při mazání záznamu!')
          }),
          onError: console.error
        }
      ),
    getAutocompleteAddress: () =>
      mutation(
        async ({
          lang = 'cs',
          limit = 10,
          type = 'regional.address',
          query
        }: {
          lang?: string;
          limit?: number;
          type?: string | string[];
          query: string;
        }): Promise<SeznamAutocomplete[] | undefined> => {
          const fetchData = await fetch(
            qsUrl('https://api.mapy.cz/v1/suggest', {
              apikey: env.SEZNAM_API_KEY,
              lang,
              limit,
              type,
              query,
              locality: 'cz'
            })
          );
          const fetchDataResponse: SeznamAutocompleteResponse = await fetchData.json();

          return fetchDataResponse.items.map((item, idx) => ({
            id: idx,
            name: item.name,
            data: item
          }));
        },
        {}
      ),
    addInsuranceCompany: () =>
      mutation<{ title: string }, ApiResponse<{ id: string }>>(
        async ({ title }) =>
          (
            await api.post<ApiResponse<{ id: string }>>(ENDPOINTS.ADD_INSURANCE_COMPANY(), {
              title
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'registry', 'insuranceCompanies']
              });
            },
            failed: () => notify('error', 'Chyba při přidávání záznamu!')
          }),
          onError: console.error
        }
      ),
    rejectDealerStatementAccident: () =>
      mutation<{ statementId: number; note?: string }, ApiResponse>(
        async ({ statementId, note }) =>
          (
            await api.patch<ApiResponse>(ENDPOINTS.REJECT_DEALER_STATEMENT_ACCIDENT(statementId), {
              note
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'statistics', 'statement', 'dealer']
              });
            },
            failed: () => notify('error', 'Chyba při úpravě záznamu!')
          }),
          onError: console.error
        }
      ),
    hideDealerStatementAccident: () =>
      mutation<{ statementId: number }, ApiResponse>(
        async ({ statementId }) =>
          (await api.patch<ApiResponse>(ENDPOINTS.HIDE_DEALER_STATEMENT_ACCIDENT(statementId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'statistics', 'statement', 'dealer']
              });
            },
            failed: () => notify('error', 'Chyba při úpravě záznamu!')
          }),
          onError: console.error
        }
      ),
    signAccidentDocument: () =>
      mutation<
        {
          document: string;
          accidentId: number;
          files?: DocumentSignRequest[];
          forceRegenerate?: boolean;
          tenantType?: TenantType;
        },
        ApiResponse
      >(
        async ({ document, accidentId, forceRegenerate, files, tenantType }) =>
          (
            await api.patch<ApiResponse>(ENDPOINTS.ACCIDENT_DOCUMENT(document), {
              accidentId,
              files,
              forceRegenerate,
              tenantType
            })
          ).data,
        {
          onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
              queryKey: getAccident(variables.accidentId.toString()).queryKey
            });
          }
        }
      ),
    assigneeServiceToAccident: () =>
      mutation<
        {
          accidentId: number;
          sourceId: number;
          note?: string;
          serviceInfo?: string;
          method?: HttpMethod;
        },
        ApiResponse
      >(
        async ({ method = 'post', accidentId, sourceId, note, serviceInfo }) => {
          const url = ENDPOINTS.ACCIDENT_SERVICE(accidentId);

          const client: Record<
            HttpMethod,
            <T>(url: string, data?: unknown) => Promise<import('axios').AxiosResponse<T>>
          > = {
            post: api.post.bind(api),
            put: api.put.bind(api),
            patch: api.patch.bind(api)
          };

          const payload = { sourceId, note, serviceInfo };
          const res = await client[method]<ApiResponse>(url, payload);
          return res.data;
        },
        {
          onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
              queryKey: getAccidentService(variables.accidentId).queryKey
            });
          }
        }
      ),
    previewServiceOrderForm: () =>
      mutation<{ accidentId: number }, ServiceOrderFormFile>(
        async ({ accidentId }) =>
          (
            await api.post<ApiResponse<ServiceOrderFormFile>>(ENDPOINTS.ACCIDENT_SERVICE_ORDER_FORM(accidentId), {
              isPreview: true
            })
          ).data.results,
        {}
      ),
    generateServiceOrderForm: () =>
      mutation<
        { accidentId: number; signature: string; handoverDate: string; takeoverDate: string },
        ServiceOrderFormResult
      >(
        async ({ accidentId, ...payload }) =>
          (
            await api.post<ApiResponse<ServiceOrderFormResult>>(
              ENDPOINTS.ACCIDENT_SERVICE_ORDER_FORM(accidentId),
              payload
            )
          ).data.results,
        {
          onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
              queryKey: getAccidentService(variables.accidentId).queryKey
            });
          }
        }
      ),
    downloadServiceOrderForm: () =>
      mutation<{ accidentId: number }, ServiceOrderFormFile>(
        async ({ accidentId }) =>
          (await api.get<ApiResponse<ServiceOrderFormFile>>(ENDPOINTS.ACCIDENT_SERVICE_ORDER_FORM(accidentId))).data
            .results,
        {}
      ),
    removeRegistryItem: () =>
      mutation<{ registryId: number; registryType: ACCIDENT_SOURCE }, ApiResponse>(
        async ({ registryId }) => (await api.delete<ApiResponse>(ENDPOINTS.ACCIDENT_SOURCES(registryId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => invalidateSources(),
            failed: () => notify('error', 'Chyba při odstraňování záznamu!')
          }),
          onError: console.error
        }
      ),
    createRegistryItem: () =>
      mutation<
        {
          registryType: ACCIDENT_SOURCE;
          data: RegistryRequest;
        },
        ApiResponse
      >(
        async ({ data }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.ACCIDENT_SOURCES(), {
              data
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Záznam byl vytvořen');
              invalidateSources();
              queryClient.invalidateQueries({
                queryKey: getRegistryList(RegistryType.ACCIDENTS_SOURCES).queryKey
              });
            },
            failed: () => notify('error', 'Chyba při tvorbě záznamu!')
          }),
          onError: console.error
        }
      ),
    editRegistryItem: () =>
      mutation<
        {
          registryId: number;
          registryType: ACCIDENT_SOURCE;
          data: RegistryRequest;
        },
        ApiResponse
      >(
        async ({ registryId, data }) =>
          (
            await api.put<ApiResponse>(ENDPOINTS.ACCIDENT_SOURCES(registryId), {
              data
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              notify('success', 'Záznam byl upraven');
              invalidateSources();
            },
            failed: () => notify('error', 'Chyba při úpravě záznamu!')
          }),
          onError: console.error
        }
      ),
    getExistingUser: () =>
      mutation<{ identification: string; type?: 'person' | 'company' }, ApiResponse<AccidentExistingUserResponse>>(
        async ({ identification, type = 'person' }) =>
          (
            await api.get<ApiResponse<AccidentExistingUserResponse>>(
              qsUrl(ENDPOINTS.EXISTING_USER(), {
                value: encodeURIComponent(identification),
                type
              })
            )
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {},
            failed: () => notify('error', 'Nenalezli jsme žádný záznam')
          }),
          onError: console.error
        }
      ),
    getEmailAccount: () =>
      mutation<{ email: string }, ApiResponse<UserEmailAccountResponse>>(
        async ({ email }) =>
          (await api.post<ApiResponse<UserEmailAccountResponse>>(ENDPOINTS.GET_ACCOUNT_EMAIL(), { email })).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {},
            failed: () => notify('error', 'Nenalezli jsme žádný záznam')
          }),
          onError: console.error
        }
      ),
    createAccount: () =>
      mutation<AddNewAccountRequest, ApiResponse>(
        async (formData) => (await api.post<ApiResponse>(ENDPOINTS.ACCOUNT(), formData)).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {},
            failed: () => notify('error', 'Nepodařilo se založit uživatele')
          }),
          onError: console.error
        }
      ),
    createCashReceipt: () =>
      mutation<{ formData: CashReceipt }, ApiResponse>(
        async ({ formData }) => (await api.post<ApiResponse>(ENDPOINTS.ACCOUNTING_DOCUMENTS(), formData)).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: async () => {
              notify('success', 'Doklad byl úspěšně vytvořen');
            },
            failed: async (_, __, options) => {
              if (options.statusCode !== 439) {
                notify('error', 'Problém u vytváření dokumentu');
              }
            }
          }),
          onSettled: async () => {
            await queryClient.invalidateQueries({
              queryKey: getCashReceipts().queryKey
            });
          },
          onError: console.error
        }
      ),
    createSourceCommissionDocuments: () =>
      mutation<CreateSourceCommissionDocumentsRequest, ApiResponse<CreateSourceCommissionDocumentsResult>>(
        async (body) =>
          (
            await api.post<ApiResponse<CreateSourceCommissionDocumentsResult>>(
              ENDPOINTS.SOURCE_COMMISSION_DOCUMENTS(),
              body
            )
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            failed: (_, __, { statusCode }) => notify('error', getErrorMessage(statusCode))
          }),
          onError: (error) => {
            console.error(error);
            notify('error', getErrorMessage(getErrorCode(error)));
          },
          onSettled: () => queryClient.invalidateQueries({ queryKey: SOURCE_COMMISSIONS_KEY })
        }
      ),
    previewSourceCommissionDocuments: () =>
      mutation<CreateSourceCommissionDocumentsRequest, ApiResponse<SourceCommissionPreviewResult>>(
        async (body) =>
          (await api.post<ApiResponse<SourceCommissionPreviewResult>>(ENDPOINTS.SOURCE_COMMISSION_PREVIEW(), body))
            .data,
        {
          onSuccess: onSuccessCustomApiResponse({
            failed: (_, __, { statusCode }) => notify('error', getErrorMessage(statusCode))
          }),
          onError: (error) => {
            console.error(error);
            notify('error', getErrorMessage(getErrorCode(error)));
          }
        }
      ),
    cancelSourceCommissions: () =>
      mutation<CancelSourceCommissionsRequest, ApiResponse<CancelSourceCommissionsResult>>(
        async (body) =>
          (await api.post<ApiResponse<CancelSourceCommissionsResult>>(ENDPOINTS.SOURCE_COMMISSION_CANCEL(), body)).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            failed: (_, __, { statusCode }) => notify('error', getErrorMessage(statusCode))
          }),
          onError: (error) => {
            console.error(error);
            notify('error', getErrorMessage(getErrorCode(error)));
          },
          onSettled: () => queryClient.invalidateQueries({ queryKey: SOURCE_COMMISSIONS_KEY })
        }
      ),
    updateSourceCommissionDocument: () =>
      mutation<{ id: number; items: SourceCommissionItemInput[] }, ApiResponse<SourceCommissionDocumentDetail>>(
        async ({ id, items }) =>
          (
            await api.put<ApiResponse<SourceCommissionDocumentDetail>>(ENDPOINTS.SOURCE_COMMISSION_DOCUMENTS(id), {
              items
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => notify('success', 'Doklad byl úspěšně upraven'),
            failed: (_, __, { statusCode }) => notify('error', getErrorMessage(statusCode))
          }),
          onError: (error) => {
            console.error(error);
            notify('error', getErrorMessage(getErrorCode(error)));
          },
          onSettled: () => queryClient.invalidateQueries({ queryKey: SOURCE_COMMISSIONS_KEY })
        }
      ),
    removeSourceCommissionDocument: () =>
      mutation<{ id: number }, ApiResponse<{ id: number }>>(
        async ({ id }) =>
          (await api.delete<ApiResponse<{ id: number }>>(ENDPOINTS.SOURCE_COMMISSION_DOCUMENTS(id))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => notify('success', 'Doklad byl úspěšně smazán'),
            failed: (_, __, { statusCode }) => notify('error', getErrorMessage(statusCode))
          }),
          onError: (error) => {
            console.error(error);
            notify('error', getErrorMessage(getErrorCode(error)));
          },
          onSettled: () => queryClient.invalidateQueries({ queryKey: SOURCE_COMMISSIONS_KEY })
        }
      ),
    removeAccountantDocument: () =>
      mutation<{ documentId: number }, ApiResponse>(
        async ({ documentId }) => (await api.delete<ApiResponse>(ENDPOINTS.ACCOUNTING_DOCUMENTS(documentId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: async () => {
              notify('success', 'Doklad byl úspěšně smazán');
              await queryClient.invalidateQueries({
                queryKey: getCashReceipts().queryKey
              });
            },
            failed: () => notify('error', 'Problém u mazání dokumentu')
          }),
          onError: console.error
        }
      ),
    updateDealerStatementsDataGrid: () =>
      mutation<SavedPrefs, ApiResponse>(
        async (data) =>
          (
            await api.put<ApiResponse>(ENDPOINTS.DEALER_STATEMENT_GRID(), {
              json: JSON.stringify(data)
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: async () => {
              await queryClient.invalidateQueries({
                queryKey: getUser().queryKey
              });
            }
          }),
          onError: console.error
        }
      ),
    editAccountantDocument: () =>
      mutation<{ documentId: number; formData: CashReceipt }, ApiResponse>(
        async ({ documentId, formData }) =>
          (await api.put<ApiResponse>(ENDPOINTS.ACCOUNTING_DOCUMENTS(documentId), formData)).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: async () => {
              notify('success', 'Doklad byl úspěšně upraven');
            },
            failed: async (_, __, options) => {
              if (options.statusCode !== 439) {
                notify('error', 'Problém u úpravy dokumentu');
              }
            }
          }),
          onError: console.error,
          onSettled: async () => {
            await queryClient.invalidateQueries({
              queryKey: getCashReceipts().queryKey
            });
          }
        }
      ),
    setDealerAvgPhmPrice: () =>
      mutation<{ year: string; month: string; price: number; liters?: number }, ApiResponse>(
        async ({ year, month, price, liters }) =>
          (
            await api.post<ApiResponse>(ENDPOINTS.DEALER_PHM_PRICE(), {
              year,
              month,
              price,
              liters
            })
          ).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: async () => {
              notify('success', 'Cena byla nastavena');
            },
            failed: async () => {
              notify('error', 'Problém u nastavení ceny');
            }
          }),
          onError: console.error,
          onSettled: async () => {
            await queryClient.invalidateQueries({
              queryKey: getUser().queryKey
            });
          }
        }
      ),
    addDealerSourceFavourite: () =>
      mutation<{ sourceId: number }, ApiResponse>(
        async ({ sourceId }) => (await api.put<ApiResponse>(ENDPOINTS.DEALER_SOURCE(sourceId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'accident', 'sources']
              });
            }
          }),
          onError: console.error
        }
      ),
    removeDealerSourceFavourite: () =>
      mutation<{ sourceId: number }, ApiResponse>(
        async ({ sourceId }) => (await api.delete<ApiResponse>(ENDPOINTS.DEALER_SOURCE(sourceId))).data,
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.invalidateQueries({
                queryKey: [...ROOT_KEY, 'accident', 'sources']
              });
            }
          }),
          onError: console.error
        }
      ),
    getUser,
    getArticle,
    getArticles,
    getForm,
    getForms,
    getRequests,
    getDocuments,
    getRecentDocuments,
    getEvents,
    getEvent,
    getQuestionnaries,
    getQuestionnaire,
    getNotifications,
    getAccidents,
    getAccident,
    getAccidentsWaiting,
    getRegistryList,
    getDealerStatistics,
    getDealerStatementStatistics,
    getDashboardStatistics,
    getCarRentals,
    getRentalCar,
    getCarRentalsCarInfo,
    getDealerRewards,
    getIncomes,
    getOpenServices,
    getAccidentService,
    getAccidentSourcesByType,
    getAccidentBySources,
    getCashReceipts,
    getSourceCommissions,
    getSourceCommissionDocuments,
    getSourceCommissionDocument,
    getAccountingDocumentsInvoices,
    getEstimatedPayments,
    getPeriodProgress,
    clearCache
  };
};
